// Pure helpers for the Season editor (list rows, bulk values, template bodies).
// Only depends on `dayjs` (a plain npm module) so it runs under `node --test`.
const dayjs = require('dayjs');

const SEASON_TABS = Object.freeze({
  PRE_SEASON: 'pre_season',
  ACTIVE: 'active',
  ARCHIVE: 'archive',
});

const TAB_STATE = Object.freeze({
  [SEASON_TABS.PRE_SEASON]: 'template',
  [SEASON_TABS.ACTIVE]: 'active',
  [SEASON_TABS.ARCHIVE]: 'archived',
});

// Sentinel shown as "Multiple values" in bulk edit.
const MULTIPLE_VALUES = '__multiple_values__';

const CROP_KEYS = ['crop_type', 'crop_variety', 'crop_feature', 'sow_date'];
const IRRIGATION_KEYS = ['irrigation_type', 'irrigation_rate'];
const FORM_KEYS = [
  'soil_type',
  'farming_type',
  ...CROP_KEYS,
  ...IRRIGATION_KEYS,
  'expiration_date',
];

const readSeasonValue = (season, key) => {
  if (CROP_KEYS.includes(key)) {
    const value = season?.crop_data?.[key] ?? '';
    return key === 'crop_type' ? String(value).toLowerCase() : value;
  }
  if (IRRIGATION_KEYS.includes(key)) {
    return season?.irrigation_system?.[key] ?? '';
  }
  if (key === 'expiration_date') {
    return season?.contract_data?.expiration_date ?? null;
  }
  return season?.[key] ?? '';
};

// {key: value} where every season agrees, MULTIPLE_VALUES where they differ.
const commonValues = (seasons) => {
  const values = {};
  FORM_KEYS.forEach((key) => {
    const all = seasons.map((season) => readSeasonValue(season, key));
    values[key] = all.every((value) => value === all[0])
      ? all[0]
      : MULTIPLE_VALUES;
  });
  return values;
};

const seasonRowKey = (season) => season.id;

const buildRow = (season, block, fieldGroup) => ({
  key: seasonRowKey(season),
  season,
  block,
  fieldGroup,
});

const rowsForBlock = (block, fieldGroup, state) =>
  (block.seasons || [])
    .filter((season) => season.state === state)
    .map((season) => buildRow(season, block, fieldGroup));

// The field header is shown for multi-block fields, and for a single-block
// field only when the field's name differs from its block's.
const shouldShowFieldHeader = (fieldGroup) => {
  const blocks = fieldGroup.blocks || [];
  return blocks.length !== 1 || blocks[0].name !== fieldGroup.name;
};

// [{fieldGroup, showHeader, rows}] for one tab; groups with no rows are dropped.
const buildSeasonGroups = (tree, tab) => {
  const state = TAB_STATE[tab];
  return (tree?.field_groups || [])
    .map((fieldGroup) => ({
      fieldGroup,
      showHeader: shouldShowFieldHeader(fieldGroup),
      rows: (fieldGroup.blocks || []).reduce(
        (rows, block) => rows.concat(rowsForBlock(block, fieldGroup, state)),
        [],
      ),
    }))
    .filter((group) => group.rows.length > 0);
};

const flattenRows = (groups) =>
  groups.reduce((rows, group) => rows.concat(group.rows), []);

// "Sep 2021" from a sow date ('YYYY-MM-DD' or anything dayjs parses).
const formatSeasonMonth = (sowDate) => {
  if (!sowDate) {
    return '';
  }
  const parsed = dayjs(sowDate);
  return parsed.isValid() ? parsed.format('MMM YYYY') : '';
};

// Single-block field (or field named like its block): just the block name;
// otherwise "Field / Block". Archive adds " / <season month>".
const seasonTitle = (row, tab) => {
  if (!row) {
    return '';
  }
  const parts = shouldShowFieldHeader(row.fieldGroup)
    ? [row.fieldGroup.name, row.block.name]
    : [row.block.name];
  if (tab === SEASON_TABS.ARCHIVE) {
    const month = formatSeasonMonth(row.season?.crop_data?.sow_date);
    if (month) {
      parts.push(month);
    }
  }
  return parts.join(' / ');
};

// Only the keys the user actually changed (and not left at "Multiple values").
const changedKeys = (initialValues, currentValues) =>
  FORM_KEYS.filter(
    (key) =>
      currentValues[key] !== MULTIPLE_VALUES &&
      currentValues[key] !== initialValues[key],
  );

const pick = (values, keys) =>
  keys.reduce((picked, key) => ({ ...picked, [key]: values[key] }), {});

// A template's PUT body: only what changed, merged onto the template's own
// nested objects (the API writes crop_data / irrigation_system as sent).
// `defaultRecommendationDisplay` is fieldManager's, passed in to stay pure.
const buildTemplateBody = (season, changes, defaultRecommendationDisplay) => {
  const body = {};
  const keys = Object.keys(changes);
  ['soil_type', 'farming_type', 'expiration_date'].forEach((key) => {
    if (keys.includes(key)) {
      body[key] = changes[key];
    }
  });
  const cropKeys = keys.filter((key) => CROP_KEYS.includes(key));
  if (cropKeys.length) {
    body.crop_data = {
      ...(season.crop_data || {}),
      ...pick(changes, cropKeys),
    };
  }
  const irrigationKeys = keys.filter((key) => IRRIGATION_KEYS.includes(key));
  if (irrigationKeys.length) {
    body.irrigation_system = {
      ...(season.irrigation_system || {}),
      ...pick(changes, irrigationKeys),
    };
    if (keys.includes('irrigation_type')) {
      body.irrigation_system.recommendation_display =
        defaultRecommendationDisplay(changes.irrigation_type);
    }
  }
  return body;
};

const SENSORS_MISSING_ERROR = 'season_sensors_missing';

const isPlainObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const sameKeys = (a, b) => {
  const left = Object.keys(a).sort();
  const right = Object.keys(b).sort();
  return (
    left.length === right.length &&
    left.every((key, index) => key === right[index])
  );
};

// The PUT v2/seasons body for one season, built from its DETAIL
// (GET v2/seasons/...), never from the tree (which has no sensors).
// - template: partial body, never any `sensors` (the API rejects them).
// - otherwise: deps.buildV1Body(detailSeason, changes) — the exact V1 body — and
//   it must carry exactly the detail's sensors. V1's update deactivates every
//   sensor missing from the request, so a body without them is never returned.
const buildSeasonPutBody = (detailSeason, changes, deps) => {
  if (detailSeason?.state === 'template') {
    return buildTemplateBody(
      detailSeason,
      changes,
      deps.defaultRecommendationDisplay,
    );
  }
  if (!isPlainObject(detailSeason?.sensors)) {
    throw new Error(SENSORS_MISSING_ERROR);
  }
  const body = deps.buildV1Body(detailSeason, changes);
  if (
    !isPlainObject(body?.sensors) ||
    !sameKeys(body.sensors, detailSeason.sensors)
  ) {
    throw new Error(SENSORS_MISSING_ERROR);
  }
  return body;
};

module.exports = {
  SENSORS_MISSING_ERROR,
  buildSeasonPutBody,
  SEASON_TABS,
  TAB_STATE,
  MULTIPLE_VALUES,
  FORM_KEYS,
  CROP_KEYS,
  IRRIGATION_KEYS,
  readSeasonValue,
  commonValues,
  shouldShowFieldHeader,
  buildSeasonGroups,
  flattenRows,
  formatSeasonMonth,
  seasonTitle,
  changedKeys,
  pick,
  buildTemplateBody,
};
